import axios from "axios";
import moment from "moment";
import { LABEL_TIPO_VEICULO, LABEL_TAE_TIPO, LABEL_TAE_TIPO_MODIFICADOR, NOMES_DIA_SEMANA } from "./Constants";

export const listaFormatoImagens = ["jpg", "png", "jpeg", "image/jpg", "image/png", "image/jpeg"];

export const tiposArquivo = {
  IMAGENS: "image/png,image/jpeg,image/jpg",
  IMAGENS_E_PDF: "image/png,image/jpeg,image/jpg,application/pdf",
  TODOS: "*/*",
};

export const textoExclusao = {
  title: "Atenção!",
  text: "Tem certeza que deseja remover?",
  icon: "warning",
  showCancelButton: true,
  confirmButtonColor: "#3085d6",
  cancelButtonColor: "#d33",
  cancelButtonText: "Não",
  confirmButtonText: "Sim",
};

export const validarTipoArquivo = (arquivo, tiposPermitidos) => {
  if (tiposPermitidos === tiposArquivo.TODOS) return true;
  
  const tiposArray = tiposPermitidos.split(",").map((t) => t.trim());
  
  return arquivo && tiposArray.includes(arquivo.type);
};

export const createFormikValidation = (formMap, values) => {
  const errors = {};

  for (let [key, value] of formMap) {
    if (!values[key] && values[key] !== 0) {
      errors[key] = "O campo " + value + " é obrigatório.";
    } else if (
      key.includes("email") &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values[key])
    ) {
      errors[key] = "Você precisa informar um e-mail válido.";
    } else if (
      (key.includes("senha") || key.includes("password")) &&
      values[key].length < 6
    ) {
      errors[key] = "A senha deve ter pelo menos 6 caracteres.";
    } else if (key.includes("cep") && values[key].length < 5) {
      errors[key] = "Utilize no mínimo 8 caracteres.";
    } else if (key.includes("cpf") && !validaCpf(values[key])) {
      errors[key] = "O CPF informado é inválido.";
    } else if (key.includes("cnpj") && !validaCnpj(values[key])) {
      errors[key] = "O CNPJ informado é inválido.";
    } else if (key.includes("telefone") && values[key].length < 14) {
      errors[key] = "Utilize no mínimo 10 números.";
    }
  }

  return errors;
};

export const shallowEqual = (object1, object2) => {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (object1[key] !== object2[key]) {
      return false;
    }
  }

  return true;
};

export const formatBRLInput = (input) => {
  input.target.value =
    Number(input.target.value.toString().replace(/[^0-9]/g, "")) / 10 ** 2;

  input.target.value = input.target.value ? formatBRL(input.target.value) : "";

  return input;
};

export const formatDateTimeLocal = (isoOrDateString) => {
  if (!isoOrDateString) return "";
  
  const momentObject = moment(isoOrDateString);
  
  return momentObject.isValid() ? momentObject.format("DD/MM/YYYY HH:mm") : "";
};

export const formatDateLocal = (isoOrDateString) => {
  if (!isoOrDateString) return "";
  
  const momentObject = moment(isoOrDateString);
  
  return momentObject.isValid() ? momentObject.format("DD/MM/YYYY") : "";
};

export const formatTimeLocal = (isoOrDateString) => {
  if (!isoOrDateString) return "";
  
  const momentObject = moment(isoOrDateString);
  
  return momentObject.isValid() ? momentObject.format("HH:mm") : "";
};

export const formatBRL = (value) => {
  const formatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

  value = formatter.format(value);

  return value;
};

/**
 * Converte string ou valor formatado em BRL para número (ex.: "R$ 400,00" -> 400).
 * Retorna "" se value for vazio ou inválido.
 */
export const parseBRL = (value) => {
  if (value === "" || value == null) return "";
  
  const n = parseFloat(String(value).replace(/\D/g, "")) / 100;
  
  return isNaN(n) ? "" : n;
};

/**
 * Formata valor numérico para exibição em BRL. Retorna "" para vazio ou inválido.
 */
export const formatBRLDisplay = (value) => {
  if (value === "" || value == null) return "";

  const num = Number(value);

  if (isNaN(num)) return "";

  return formatBRL(num);
};

/**
 * Formata número para exibição em pt-BR (ex.: km rodados). Retorna "-" para vazio ou inválido.
 */
export const formatKm = (value) => {
  if (value === "" || value == null) return "-";

  const num = Number(value);

  if (isNaN(num)) return "-";

  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatHourInput = (input) => {
  let value = input.target.value;

  value = value.replace(/\D/g, "");
  value = value.replace(/(\d{2})(\d)/, "$1:$2");

  input.target.value = value;

  return input;
};

export const formatCepInput = (input) => {
  let value = input.target.value;

  value = value.replace(/\D/g, "");
  value = value.replace(/(\d{5})(\d)/, "$1-$2");

  input.target.value = value;

  return input;
};

export const formatCep = (value) => {
  value = value?.replace(/\D/g, "");
  value = value?.replace(/(\d{5})(\d)/, "$1-$2");
  
  return value;
};

export const formatCPFInput = (input) => {
  let value = input.target.value;

  value = value.replace(/\D/g, "");
  value = value.replace(/(\d{3})(\d)/, "$1.$2");
  value = value.replace(/(\d{3})(\d)/, "$1.$2");
  value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2");

  input.target.value = value;

  return input;
};

export const formatCPF = (cpf) => {
  if (typeof cpf !== "string") return "";
  
  cpf = cpf.replace(/\D/g, "");
  cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
  cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
  cpf = cpf.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  
  return cpf;
};

export const formatDataInput = (input) => {
  let value = input.target.value;

  value = value.replace(/\D/g, "");
  value = value.replace(/(\d{2})(\d)/, "$1/$2");
  value = value.replace(/(\d{2})(\d)/, "$1/$2");

  input.target.value = value;

  return input;
};

export const formatIntegerInput = (input) => {
  input.target.value = Number(
    input.target.value.toString().replace(/[^0-9]/g, ""),
  );

  if (parseInt(input.target.value) === 0) input.target.value = "";

  return input;
};

export const formatPercentageFloatInput = (input) => {
  if (!input.target.value) {
    input.target.value = "";
    return input;
  }

  let value = parseFloat(input.target.value.toString().replace(/[^0-9]/g, ""));

  if (isNaN(value)) {
    input.target.value = "";

    return input;
  }

  value = value / 100;

  if (value === 0 && input.target.value.startsWith("0")) {
    value = 0;
  }

  if (isNaN(value)) {
    throw new Error("Invalid input: Please enter a valid number.");
  }

  if (value < 0) {
    throw new Error("Percentage cannot be negative.");
  }

  if (value > 100) {
    value = 100;
  }

  input.target.value = value.toFixed(2);
  input.target.value = input.target.value.replace(".", ",");

  return input;
};

export const formatPercentageFloat = (valueToConvert) => {
  if (!valueToConvert) {
    return "";
  }

  let value = parseFloat(valueToConvert.toString().replace(/[^0-9]/g, ""));

  if (isNaN(value)) {
    valueToConvert = "";

    return valueToConvert;
  }

  value = value / 100;

  if (value === 0 && valueToConvert.startsWith("0")) {
    value = 0;
  }

  if (isNaN(value)) {
    throw new Error("Invalid input: Please enter a valid number.");
  }

  if (value < 0) {
    throw new Error("Percentage cannot be negative.");
  }

  if (value > 100) {
    value = 100;
  }

  valueToConvert = value.toFixed(2);
  valueToConvert = valueToConvert.replace(".", ",");

  return valueToConvert;
};

export const formatTelefoneInput = (input) => {
  let value = input.target.value;

  value = value.replace(/\D/g, "");
  value = value.replace(/(\d{0})(\d)/, "$1($2");
  value = value.replace(/(\d{2})(\d)/, "$1) $2");
  value = value.replace(/(\d{1})(\d{1,4})$/, "$1-$2");

  input.target.value = value;

  return input;
};

export const formatTelefone = (tel) => {
  if (typeof tel !== "string") return "";
  
  tel = tel.replace(/\D/g, "");
  tel = tel.replace(/(\d{0})(\d)/, "$1($2");
  tel = tel.replace(/(\d{2})(\d)/, "$1) $2");
  tel = tel.replace(/(\d{1})(\d{1,4})$/, "$1-$2");
  
  return tel;
};

export const buscaCep = (
  input,
  setFieldValue,
  nomeEndereco,
  nomeBairro,
  nomeCidade,
  nomeUf,
) => {
  if (input.target.value.length > 8) {
    var cep = input.target.value.replace(/[^0-9]/, "");

    const instance = axios.create();

    instance
      .get(`https://viacep.com.br/ws/${cep}/json`, {})
      .then((resposta) => {
        if (resposta.data.erro) {
          setFieldValue(nomeEndereco, "");
          setFieldValue(nomeBairro, "");
          setFieldValue(nomeCidade, "");
          setFieldValue(nomeUf, "AC");
        } else {
          setFieldValue(nomeEndereco, resposta.data.logradouro);
          setFieldValue(nomeBairro, resposta.data.bairro);
          setFieldValue(nomeCidade, resposta.data.localidade);
          setFieldValue(nomeUf, resposta.data.uf);
        }
      })
      .catch((erro) => {});
  }

  return formatCepInput(input);
};

/* eslint-disable */
export const validaCpf = (cpf) => {
  var add = 0;
  var rev = 0;

  cpf = cpf.replace(/[^\d]+/g, "");
  
  if (cpf == "") return false;

  if (cpf.length != 11) return false;

  // Verifica se todos os dígitos são iguais (ex: 111.111.111-11)
  if (/^(\d)\1+$/.test(cpf)) {
    return false;
  }

  for (var i = 0; i < 9; i++) {
    add += parseInt(cpf.charAt(i)) * (10 - i);
  }

  rev = 11 - (add % 11);

  if (rev == 10 || rev == 11) {
    rev = 0;
  }
  if (rev != parseInt(cpf.charAt(9))) {
    return false;
  }

  add = 0;

  for (var j = 0; j < 10; j++) {
    add += parseInt(cpf.charAt(j)) * (11 - j);
  }

  rev = 11 - (add % 11);

  if (rev == 10 || rev == 11) {
    rev = 0;
  }

  if (rev != parseInt(cpf.charAt(10))) {
    return false;
  }

  return true;
};
/* eslint-enable */

export const validaCnpj = (cnpj) => {
  cnpj = cnpj.replace(/[^\d]+/g, "");

  if (cnpj.length !== 14) {
    return false;
  }

  if (/^(\d)\1+$/.test(cnpj)) {
    return false;
  }

  let add = 0;
  let rev = 0;
  let pos = 5;

  for (let i = 0; i < 12; i++) {
    add += parseInt(cnpj.charAt(i)) * pos;
    pos = pos === 2 ? 9 : pos - 1;
  }

  rev = add % 11 < 2 ? 0 : 11 - (add % 11);

  if (rev !== parseInt(cnpj.charAt(12))) {
    return false;
  }

  add = 0;
  pos = 6;

  for (let i = 0; i < 13; i++) {
    add += parseInt(cnpj.charAt(i)) * pos;
    pos = pos === 2 ? 9 : pos - 1;
  }

  rev = add % 11 < 2 ? 0 : 11 - (add % 11);

  return rev === parseInt(cnpj.charAt(13));
};

export const formatCNPJInput = (input) => {
  let value = input.target.value;

  value = value.replace(/\D/g, "");
  value = value.replace(/(\d{2})(\d)/, "$1.$2");
  value = value.replace(/(\d{3})(\d)/, "$1.$2");
  value = value.replace(/(\d{3})(\d)/, "$1/$2");
  value = value.replace(/(\d{4})(\d{1,2})$/, "$1-$2");

  input.target.value = value;

  return input;
};

export const formatCNPJ = (cnpj) => {
  if (typeof cnpj !== "string") return "";
  
  cnpj = cnpj.replace(/\D/g, "");
  cnpj = cnpj.replace(/(\d{2})(\d)/, "$1.$2");
  cnpj = cnpj.replace(/(\d{3})(\d)/, "$1.$2");
  cnpj = cnpj.replace(/(\d{3})(\d)/, "$1/$2");
  cnpj = cnpj.replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  
  return cnpj;
};

export const limparMoeda = (valor) => {
  valor = valor.replaceAll("R$", "");
  valor = valor.replaceAll(".", "");
  valor = valor.replaceAll(",", ".");
  valor = valor.replaceAll("/^p{Z}+|p{Z}+$/u", "");
  valor = valor.trim();

  return valor;
};

export const sanitizeString = (string, manter_espacos = false) => {
  if (!string) return "";
  
  const regex = manter_espacos ? /[^a-zA-Z0-9\s]/g : /[^a-zA-Z0-9]/g;
  
  return String(string).replace(regex, "");
};

export const validateFullName = (name) => {
  // Trim any leading/trailing spaces
  const trimmedName = name.trim();

  // Split the name by spaces (whitespace)
  const nameParts = trimmedName.split(/\s+/); // This splits by one or more spaces

  // Check if there are more than two parts and first & second are non-empty
  if (
    nameParts.length >= 2 &&
    nameParts[0].length > 0 &&
    nameParts[1].length > 0
  ) {
    return true; // Valid first and last name
  }

  return false; // Invalid input
};

export const getTipoVeiculoLabel = (value) => {
  return LABEL_TIPO_VEICULO[value] || "-";
}

export const getTipoTarifaEspecialLabel = (value) => {
  return (value && LABEL_TAE_TIPO[value]) || value || "-";
}

export const getTipoModificadorTarifaEspecialLabel = (value) => {
  return (value && LABEL_TAE_TIPO_MODIFICADOR[value]) || value || "-";
}

export const formatDiasSemanaTarifaEspecial = (dias) => {
  if (!dias || !Array.isArray(dias) || dias.length === 0) return "-";
  
  return dias.map((d) => NOMES_DIA_SEMANA[d] || d).join("; ");
}

export const formatTimeTarifaEspecial = (value) => {
  const parsed = timeToInputValueTarifaEspecial(value);
  return parsed || "-";
}

/**
 * Converte valor de horário (ex.: "14:30:00" ou ISO "2026-02-13T15:00:00.000000Z") para valor de input type="time" ("HH:mm").
 * Retorna "" se value for vazio ou inválido.
 */
export const timeToInputValueTarifaEspecial = (value) => {
  if (value == null || value === "") return "";

  const str = String(value).trim();
  
  if (str.indexOf("T") !== -1) {
    const m = moment.utc(value, moment.ISO_8601, true);
    
    if (m.isValid()) return m.format("HH:mm");
  }

  const m = moment(value, ["HH:mm", "HH:mm:ss"], true);
  
  if (m.isValid()) return m.format("HH:mm");

  if (str.length >= 5 && /^\d{1,2}:\d{2}/.test(str)) {
    const parts = str.match(/^(\d{1,2}):(\d{2})/);
    
    if (parts) return `${parts[1].padStart(2, "0")}:${parts[2]}`;
  }
  
  return "";
}

/**
 * Converte data (ISO "2026-02-13T03:00:00.000000Z" ou "YYYY-MM-DD") para valor de input type="date" ("YYYY-MM-DD").
 * Retorna null se value for vazio ou inválido.
 */
export const parseDate = (value) => {
  if (value == null || value === "") return null;

  const m = moment(value, moment.ISO_8601, true);
  
  if (m.isValid()) return m.format("YYYY-MM-DD");

  const str = String(value).trim();
  
  if (moment(str, "YYYY-MM-DD", true).isValid()) return str.substring(0, 10);
  
  return null;
}

/**
 * Converte string de data (YYYY-MM-DD) em instância Date para uso em Calendar/date picker.
 * Retorna null se dateStr for vazio ou inválido.
 */
export const dateToCalendarValue = (dateStr) => {
  if (!dateStr) return null;

  const m = moment(dateStr, ["YYYY-MM-DD", moment.ISO_8601], true);
  
  return m.isValid() ? m.toDate() : null;
}

/**
 * Converte Date ou valor de date picker em string YYYY-MM-DD.
 * Retorna null se date for vazio ou inválido.
 */
export const calendarToDateStr = (date) => {
  if (!date) return null;

  const m = moment(date);
  
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
}

/**
 * Converte string para capitalize (primeira letra maiúscula e as demais minúsculas).
 * Retorna "" se str for vazio ou inválido.
 */
export const capitalizeString = (str) => {
  if (str == null || typeof str !== "string") return "";
  
  const s = str.trim();
  
  if (!s) return "";
  
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};
